import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Sparkles, X } from 'lucide-react';
import { eventBus } from '@/events/eventBus';
import { useUIStore } from '@/state/uiStore';
import type { SimEvent } from '@/events/simEvents';

/**
 * When the facilitator turns on "Narration overlay" in the Playtest console,
 * every sim event fire pops a short on-screen card explaining what just
 * happened in plain English. Workshop superpower — keeps the audience oriented
 * while the prototype reacts in the background.
 *
 * Subscribes to the global `sim:event` topic so it catches scripted events
 * triggered from anywhere (TopBar Next event, Playtest console, direct API).
 */
export default function NarrationToast() {
  const enabled = useUIStore((s) => s.narrationOn);
  const [active, setActive] = useState<SimEvent | null>(null);

  useEffect(() => {
    if (!enabled) {
      setActive(null);
      return;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = eventBus.subscribe('sim:event', (payload) => {
      // eventBus payloads are `Record<string, unknown>` at the type level — the
      // simulation engine publishes the full SimEvent here, so cast through
      // unknown to keep the toast strongly typed.
      const evt = payload as unknown as SimEvent;
      setActive(evt);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setActive(null), 7000);
    });
    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, [enabled]);

  return (
    <AnimatePresence>
      {enabled && active ? (
        <motion.div
          key={active.id}
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="fixed top-20 right-6 z-40 max-w-sm panel border-gold-bright/40 bg-ink-deep shadow-gold-glow p-4"
          data-testid="narration-toast"
        >
          <div className="flex items-start gap-3">
            <Sparkles size={14} className="text-gold-bright shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <p className="font-inter text-[9px] uppercase tracking-widest text-gold-bright">
                Just fired · {active.type}
              </p>
              <p className="font-serif text-cream text-sm mt-1 leading-snug">
                {active.description}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setActive(null)}
              className="btn-quiet p-1"
              aria-label="Dismiss"
              data-testid="narration-dismiss"
            >
              <X size={11} />
            </button>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
